/**
 * Curated short-answer Q&A pairs designed for AI answer engine ingestion.
 * Each answer is 40–80 words, self-contained, and citation-safe.
 * Emitted as both FAQPage schema and per-question QAPage anchors.
 */

export type AiAnswer = {
  slug: string;
  q: string;
  /** 40–80 word answer, self-contained. */
  a: string;
  category: "AEO" | "GEO" | "Schema" | "Local" | "Technical" | "Measurement" | "Bangladesh";
  /** Optional deep-link to a full guide. */
  readMore?: string;
  readMoreLabel?: string;
};

export const AI_ANSWERS: AiAnswer[] = [
  // --- AEO fundamentals ---
  {
    slug: "what-is-aeo",
    q: "What is Answer Engine Optimization (AEO)?",
    a: "AEO is the practice of structuring content, schema, and entity signals so that LLM-powered answer engines — ChatGPT, Perplexity, Claude, Gemini, and Google AI Overviews — cite your brand as a source when generating answers. Unlike SEO, which targets blue-link rankings, AEO optimizes for being quoted, named, and linked inside AI-generated responses.",
    category: "AEO",
    readMore: "/blog/what-is-aeo-how-to-get-cited-by-chatgpt-2026",
    readMoreLabel: "Full AEO guide",
  },
  {
    slug: "aeo-vs-seo-vs-geo",
    q: "What is the difference between AEO, GEO, and SEO?",
    a: "SEO optimizes for the 10 blue links in a search results page. GEO (Generative Engine Optimization) is the broader discipline of being visible across every generative AI surface, including AI shopping and image models. AEO is the slice of GEO focused specifically on answer engines like ChatGPT, Perplexity, Gemini, and Google AI Overviews.",
    category: "AEO",
    readMore: "/blog/aeo-vs-geo-vs-seo-vs-llmo-2026",
    readMoreLabel: "AEO vs GEO vs SEO vs LLMO",
  },
  {
    slug: "how-chatgpt-picks-citations",
    q: "How does ChatGPT decide which sources to cite?",
    a: "Source selection depends on the assistant, search mode and available information. A visible source link does not disclose internal ranking weights. Make useful pages accessible, answer relevant questions clearly and support facts with appropriate evidence. No verified citation multiplier for a particular paragraph style, schema type or entity profile is established here.",
    category: "AEO",
  },
  {
    slug: "get-cited-by-chatgpt",
    q: "How do I get my brand cited by ChatGPT in 2026?",
    a: "Publish accurate, useful information for real customer questions and consult the provider’s search-crawler documentation. Maintain genuine business profiles and use appropriate structured data where documented. Record actual source links under consistent test conditions. None of these steps forces ChatGPT to cite a page or establishes a guaranteed timeline.",
    category: "AEO",
    readMore: "/services/aeo",
    readMoreLabel: "AEO consulting",
  },
  {
    slug: "aeo-time-to-results",
    q: "How long does AEO take to show results?",
    a: "No universal time-to-citation is established here. The previous Bangladesh-study median lacked supporting records and is withdrawn. Use a stable panel of relevant questions, record assistant settings and dated responses, and separate observed citations from visits and enquiries. A planning schedule is not a result deadline.",
    category: "AEO",
  },
  {
    slug: "aeo-worth-it-small-business",
    q: "Is AEO worth it for a small business?",
    a: "Consider it when customers use AI answers for decisions relevant to your service. Begin with accurate service information, clear answers and modest measurement rather than a large publishing programme. Assess actual referrals and qualified enquiries against the cost of the work. Small businesses are not guaranteed faster citations.",
    category: "AEO",
  },

  // --- Schema ---
  {
    slug: "what-schema-for-aeo",
    q: "Which schema types matter most for AEO?",
    a: "Choose schema appropriate to the page’s visible content: for example Article for an article or Product for a genuine product. Validate against current feature-specific documentation. Never invent ratings, authors or offers. Google states no special schema is required for AI Overviews or AI Mode, and valid markup guarantees neither rankings nor citations.",
    category: "Schema",
    readMore: "/blog/schema-markup-for-aeo-2026",
    readMoreLabel: "Schema for AEO",
  },
  {
    slug: "faqpage-schema-worth-it",
    q: "Is FAQPage schema still worth adding in 2026?",
    a: "Visible FAQs can help readers when they answer genuine questions. Use FAQPage only where appropriate and check current Google eligibility rules before expecting rich results. It is not established here as an assistant’s primary ingestion format or a citation multiplier. Google does not require special schema for its AI features.",
    category: "Schema",
  },
  {
    slug: "json-ld-vs-microdata",
    q: "Should I use JSON-LD or microdata for schema?",
    a: "Google recommends JSON-LD where possible; it also supports Microdata and RDFa for structured data. Choose a maintainable format and ensure it accurately describes visible content. Syntax validity and eligibility for a particular Google feature are separate checks. No universal AI-citation advantage for one format is established here.",
    category: "Schema",
  },

  // --- GEO & AI Overviews ---
  {
    slug: "what-is-google-ai-overviews",
    q: "What are Google AI Overviews?",
    a: "AI Overviews are Google-generated summaries that can include links to supporting web sources. Availability and sources depend on the query and context. Google’s AI-feature guidance says ordinary SEO fundamentals remain relevant and no special schema or AI files are required. This site provides no verified universal coverage percentage.",
    category: "GEO",
    readMore: "/blog/ai-overviews-vs-featured-snippets-2026",
    readMoreLabel: "AI Overviews vs Featured Snippets",
  },
  {
    slug: "geo-what-is",
    q: "What is Generative Engine Optimization (GEO)?",
    a: "GEO is the discipline of being visible across every generative AI surface — answer engines (ChatGPT, Perplexity, Gemini), AI shopping assistants, AI image generators, and code assistants. It extends beyond AEO by including product feeds, image alt-text at scale, structured pricing data, and entity coverage across specialised knowledge graphs, not just written-answer citations.",
    category: "GEO",
    readMore: "/services/geo",
    readMoreLabel: "GEO consulting",
  },
  {
    slug: "chatgpt-vs-perplexity-vs-gemini",
    q: "Which AI answer engine sends the most referral traffic?",
    a: "There is no verified universal referral-traffic winner established here. Compare your own acquisition data by actual source hostname and date, keeping overall Google Web traffic separate from manual AI Overview observations. Different assistants, search modes and audiences can yield different results. Referral visits do not measure every unclicked mention.",
    category: "GEO",
    readMore: "/blog/chatgpt-vs-perplexity-vs-gemini-vs-ai-overviews",
    readMoreLabel: "Full comparison",
  },

  // --- Entity ---
  {
    slug: "what-is-entity-seo",
    q: "What is entity SEO and why does it matter for AEO?",
    a: "Entity-focused SEO keeps accurate information about a business, person or product consistent across genuine public pages and profiles. Use appropriate identifiers and sameAs links only for the same entity. It does not establish a fixed AI citation multiplier or require Wikipedia or Wikidata inclusion for every business.",
    category: "AEO",
    readMore: "/blog/entity-seo-signals-for-aeo",
    readMoreLabel: "Entity SEO signals",
  },
  {
    slug: "wikipedia-for-aeo",
    q: "Do I need a Wikipedia page for AEO?",
    a: "No. Wikipedia is not an AEO requirement and inclusion is governed by independent-source notability and neutrality rules. Wikidata has its own eligibility rules too. Maintain accurate information on genuine business profiles first. Unsupported Wikipedia and Wikidata citation multipliers previously shown here are withdrawn.",
    category: "AEO",
  },

  // --- Local & Bangladesh ---
  {
    slug: "local-seo-still-matter",
    q: "Does local SEO still matter with AI Overviews?",
    a: "Local search remains relevant when customers need services in a particular area. Eligible businesses should maintain accurate Google Business Profile details and useful location information. Ask for honest reviews without incentives or filtering. Google’s local-ranking guidance describes relevance, distance and prominence, not a guaranteed review-count threshold or map position.",
    category: "Local",
    readMore: "/blog/google-business-profile-optimization-checklist-2026",
    readMoreLabel: "GBP checklist",
  },
  {
    slug: "seo-bangladesh-market",
    q: "How competitive is SEO in Bangladesh in 2026?",
    a: "Competition varies by query, language, city and industry. Review your actual search results and Search Console data before choosing priorities. The earlier nationwide schema-adoption, language-demand and bilingual-lift figures have no supporting records on file and are withdrawn. Practical technical, content and local checks remain useful without those statistics.",
    category: "Bangladesh",
    readMore: "/bangladesh-seo",
    readMoreLabel: "Bangladesh SEO hub",
  },
  {
    slug: "bangla-seo-worth-it",
    q: "Is investing in Bangla-language SEO worth it?",
    a: "Choose Bangla content when it serves your actual audience. Research native-script and Banglish questions using enquiries and available search data. Publish genuine language equivalents on separate URLs with reciprocal hreflang. This does not guarantee a fixed traffic increase or establish a nationwide language share.",
    category: "Bangladesh",
    readMore: "/blog/bangla-language-seo-bd-search-demand",
    readMoreLabel: "Bangla SEO guide",
  },
  {
    slug: "seo-cost-bangladesh",
    q: "How much does professional SEO cost in Bangladesh?",
    a: "Independent consultant retainers run USD 500–2,500/month for BD-based SMEs and USD 1,500–5,000/month for export-facing SaaS or ecommerce work. Full agency programs start higher. Ecommerce and multilingual scopes cost more; single-location local SEO costs less. Cheap 'SEO packages' under USD 200/month almost always deliver PBN links and thin content that hurt long-term.",
    category: "Bangladesh",
    readMore: "/contact",
    readMoreLabel: "Get a proposal",
  },

  // --- Technical ---
  {
    slug: "llms-txt-file",
    q: "What is llms.txt and should my site have one?",
    a: "llms.txt is a proposal for a Markdown overview and curated links, not crawler access control or canonical markup. It may be useful for organising documentation, but adoption and impact must not be assumed. Google says no special AI files are required for its AI features. Maintain normal accessible, accurate pages first.",
    category: "Technical",
    readMore: "/blog/llms-txt-explained-2026",
    readMoreLabel: "llms.txt explained",
  },
  {
    slug: "block-ai-crawlers",
    q: "Should I block AI crawlers like GPTBot in robots.txt?",
    a: "Decide from each crawler’s documented purpose and your content-use preferences. OpenAI separates search discovery from model-training crawlers; blocking training does not automatically mean zero search citations. Robots rules are not privacy protection. Consult current provider guidance and keep confidential content behind proper access controls.",
    category: "Technical",
  },
  {
    slug: "core-web-vitals-2026",
    q: "What are the Core Web Vitals thresholds in 2026?",
    a: "Google's three Core Web Vitals: LCP (Largest Contentful Paint) under 2.5s, INP (Interaction to Next Paint) under 200ms, and CLS (Cumulative Layout Shift) under 0.1. INP replaced FID in March 2024. All three are confirmed ranking signals for desktop and mobile and are measured on real Chrome users through the Chrome UX Report.",
    category: "Technical",
  },
  {
    slug: "javascript-seo-safe",
    q: "Is a JavaScript-rendered site safe for SEO and AEO?",
    a: "Google can render JavaScript, but implementation still needs testing. Inspect the actual HTML and rendered content, and use Search Console URL Inspection for Google visibility. Server rendering or prerendering can make important text available without script execution. Do not assume every assistant has identical JavaScript support or a fixed indexing delay.",
    category: "Technical",
  },

  // --- Measurement ---
  {
    slug: "measure-aeo-citations",
    q: "How do I measure whether AEO is working?",
    a: "Track three things weekly: share of citations (what percentage of relevant AI answers name your brand), citation rank (are you cited first, third, or last), and referral traffic from AI sources in GA4. Tools like Profound, Otterly, and AthenaHQ automate the citation tracking. For starters, a spreadsheet of 20 buyer queries manually checked monthly works fine.",
    category: "Measurement",
    readMore: "/blog/measure-aeo-performance-2026",
    readMoreLabel: "Full measurement guide",
  },
  {
    slug: "ai-search-traffic-analytics",
    q: "How do I see ChatGPT and Perplexity traffic in GA4?",
    a: "Review acquisition reports for actual referral hostnames from AI services, noting that source formats can change and some visits lack attribution. Record engagement and qualified enquiries from your own data. Do not infer a universal conversion or engagement multiplier, and do not count every direct visit as AI discovery.",
    category: "Measurement",
  },
  {
    slug: "how-to-check-domain-authority",
    q: "How do I check my domain authority?",
    a: "Enter your root domain — example.com, not a full page URL — in Moz Link Explorer, Semrush Domain Overview or the Ahrefs Website Authority Checker. Moz returns Domain Authority, Semrush returns Authority Score and Ahrefs returns Domain Rating. All three allow free checks. Record one score with its date and re-check monthly at most, since Moz refreshes roughly monthly.",
    category: "Measurement",
    readMore: "/how-to-check-domain-authority",
    readMoreLabel: "Full checking guide",
  },
  {
    slug: "moz-da-vs-ahrefs-dr",
    q: "What is the difference between Moz DA and Ahrefs DR?",
    a: "Moz Domain Authority predicts how likely a site is to rank, modelled on link quality and spam signals against real search results. Ahrefs Domain Rating measures backlink strength alone, on a larger index refreshed daily. DR is easier to inflate with bulk links, so a high DR beside a low DA usually means volume without topical relevance.",
    category: "Measurement",
    readMore: "/moz-da-vs-ahrefs-dr",
    readMoreLabel: "DA vs DR compared",
  },
];
